import { useQuery, useMutation, useQueryClient } from 'react-query';
import request from 'app/api/request';
import { errorHandler, showMessage } from 'app/utils/messages';
import { i18n } from 'app/utils/i18n';
import { v4 as uuidv4 } from 'uuid';
import { queryKeys as styleQueryKeys } from '../style-queries';
import ServerErrorHandler from 'app/ErrorHandler';
import { EventType, dispatchMessage } from 'app/useToasts';
export const queryKeys = {
  documents: 'DOCUMENTS',
  document: 'DOCUMENT',
  versions: 'VERSIONS',
  clonePreview: 'CLONE_PREVIEW',
};

export const useClonePreview = (documentSuperId, documentRevisionId) =>
  useQuery(
    [queryKeys.clonePreview, documentSuperId, documentRevisionId],
    () =>
      request
        .get(`/gaby/documents/${documentSuperId}/clone-preview?revisionId=${documentRevisionId}`)
        .then((response) => response.data)
        .catch((e) => {
          errorHandler(e);
          throw new Error('');
        }),
    { enabled: !!(documentSuperId && documentRevisionId) }
  );

export const useGetDocumentVersions = (documentId, documentRevisionId, draftDocument) =>
  useQuery(
    [queryKeys.versions, { documentId, draftDocument, documentRevisionId }],
    () => {
      return request
        .get(`/gaby/versions/${documentId}`)
        .then((res) => res.data)
        .then((versions) => {
          if (!versions.find((d) => d.documentRevisionId === draftDocument.revisionId)) {
            versions.push({
              documentSuperId: draftDocument.superId,
              documentRevisionId: draftDocument.revisionId,
              createdAt: draftDocument.createdAt,
              draft: true,
              name: 'Current draft',
            });
          }
          versions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          if (documentRevisionId) {
            versions.find((d) => d.documentRevisionId === documentRevisionId).selected = true;
          } else {
            versions[0].selected = true;
          }
          return versions;
        });
    },
    { enabled: !!draftDocument }
  );

export const useSectionReorder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ documentId, sectionId, sectionOrder }) =>
      request
        .post(`/gaby/documents/${documentId}/actions?compileSass=true`, {
          action: 'set_section_order',
          data: {
            sectionId,
            sectionOrder,
          },
        })
        .catch((e) => {
          errorHandler(e);
          throw new Error('');
        }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.document);
      },
    }
  );
};

export const useGetDocuments = (selectedOrganization, showDeleted, orderBy, orderDirection) =>
  useQuery([queryKeys.documents, { selectedOrganization, showDeleted, orderBy, orderDirection }], () => {
    return request
      .get(`/gaby/documents?current=true&organization=${selectedOrganization}&showDeleted=${showDeleted}&orderBy=${orderBy}&orderDirection=${orderDirection}`)
      .then((response) => response.data)
      .catch((e) => {
        errorHandler(e);
        throw new Error('');
      });
  });

export const useGetDocumentBySuperId = (documentSuperId) => {
  return useQuery(
    [queryKeys.document, { documentSuperId }],
    async () => {
      const response = await request.get(`/gaby/documents?superId=${documentSuperId}&current=true`);
      return response.data.length ? response.data[0] : null;
    },
    { refetchOnWindowFocus: false }
  );
};

export const useGetDocument = (documentId, selectedOrganization, revisionId, useDraft = false, embedd = false) =>
  useQuery(
    [queryKeys.document, documentId, { selectedOrganization, revisionId }],
    () => {
      const queryString = `organization=${selectedOrganization}&compileSass=true&useDraft=${useDraft}&embedd=${embedd}&showDeleted=true`;
      const path = revisionId ? `/${revisionId}?${queryString}` : `?superId=${documentId}&current=true&${queryString}`;

      return request
        .get(`/gaby/documents${path}`)
        .then((res) => {
          if (res.data.length) {
            const doc = res.data[res.data.length - 1];
            return doc;
          }
          return res.data;
        })
        .catch((e) => {
          errorHandler(e);
          throw new Error('');
        });
    },
    { enabled: !!(selectedOrganization && documentId) }
  );

export const useGetDocumentTableOfContent = (
  revisionId,
  {
    start = undefined,
    stop = undefined,
    currentPosition = undefined,
    sectionSuperId = undefined,
    headerHierarchy = undefined,
    locale = undefined,
    sectionRevisionId = undefined,
  } = {}
) => {
  const headers = headerHierarchy || ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const params = { start, stop, currentPosition, sectionSuperId, locale, sectionRevisionId, headerHierarchy: headers.join(',') };
  Object.keys(params).forEach((key) => (params[key] === undefined ? delete params[key] : {}));

  const searchParams = new URLSearchParams(params);
  return useQuery({
    queryKey: [queryKeys.document, { revisionId, locale, start, stop, currentPosition, sectionSuperId, headerHierarchy, sectionRevisionId }],
    queryFn: () => {
      if (revisionId === null) {
        return Promise.resolve(null);
      }
      return request
        .get(`/gaby/documents/${revisionId}/table-of-content?${searchParams}`)
        .then((res) => res.data)
        .catch((e) => {
          errorHandler(e);
          throw new Error('');
        });
    },
    keepPreviousData: true,
  });
};

export const useCreateTemplateStyle = (documentId) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ template }) => {
      const newStyle = {
        id: uuidv4(),
        documentSuperId: documentId,
        content: template.style,
      };

      await request.post(`/gaby/styles`, newStyle);

      const response = await request.post(`/gaby/documents/${documentId}/actions?compileSass=true`, {
        action: 'create_template_style',
        revisionId: newStyle.id,
        data: {
          styleId: newStyle.id,
          templateSuperId: template.superId,
          templateRevisionId: template.revisionId,
        },
      });

      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(styleQueryKeys.css);
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );
};

export const useCreateFontStyle = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ documentSuperId, fileSuperId }) => {
      return request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'create_font_style',
        data: {
          fileSuperId,
        },
      });
    },
    {
      onSuccess: (_, { documentSuperId }) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n(`project-details.font-created.success`),
          removeTime: 4000,
        });
        queryClient.invalidateQueries(queryKeys.document, documentSuperId);
      },
      onError: (error) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n(`project-details.font-created.error`),
          removeTime: 4000,
        });
        errorHandler(error);
      },
    }
  );
};

export const useCloneDocument = () =>
  useMutation(
    ({ documentSuperId, documentRevisionId, selectedOrganization, rollForward }) => {
      return request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'clone_document',
        organization: selectedOrganization,
        revisionId: documentRevisionId,
        superId: documentSuperId,
        ...(rollForward ? { rollForward } : {}),
      });
    },
    {
      onSuccess: () => {
        showMessage('success', i18n('project-details.document-copy-success'));
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );

export const usePageNumberingSave = () =>
  useMutation(
    ({ documentSuperId, documentRevisionId, pageNumbering }) =>
      request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'update_document_page_numbering',
        data: {
          revisionId: documentRevisionId,
          superId: documentSuperId,
          pageNumbering,
        },
      }),
    {
      onError: (error) => {
        errorHandler(error);
      },
    }
  );

export const useDocumentAreaSave = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentSuperId, documentRevisionId, header, footer }) =>
      request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'update_document_area',
        data: {
          revisionId: documentRevisionId,
          superId: documentSuperId,
          header,
          footer,
        },
      }),

    {
      onSuccess: (data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n(`document-area-${variables.footer ? 'footer' : 'header'}.save-success`),
          removeTime: 4000,
        });
        queryClient.invalidateQueries({ queryKey: [queryKeys.document] });
      },
      onError: (error, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n(`document-area-${variables.footer ? 'footer' : 'header'}.save-error`),
          removeTime: 4000,
        });
        errorHandler(error);
      },
    }
  );
};

export const useDocumentMetadataSave = () =>
  useMutation(
    ({ documentSuperId, documentRevisionId, metadata }) =>
      request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'update_document_metadata',
        data: {
          revisionId: documentRevisionId,
          superId: documentSuperId,
          metadata,
        },
      }),
    {
      onError: (error) => {
        errorHandler(error);
      },
    }
  );

export const useChangeDocumentName = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentSuperId, newName }) => {
      return request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'update_document_name',
        newName: newName,
      });
    },
    {
      onSuccess: () => {
        showMessage('success', i18n('project-list.document-rename-success'));
        queryClient.invalidateQueries(queryKeys.documents);
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentSuperId, selectedOrganization }) => {
      return request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'delete_document',
        organization: selectedOrganization,
      });
    },
    {
      onSuccess: () => {
        showMessage('success', i18n('project-list.document-delete-success'));
        queryClient.invalidateQueries(queryKeys.documents);
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );
};

export const useRestoreDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentSuperId, selectedOrganization }) => {
      return request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'restore_document',
        organization: selectedOrganization,
      });
    },
    {
      onSuccess: () => {
        showMessage('success', i18n('project-list.document-restore-success'));
        queryClient.invalidateQueries(queryKeys.documents);
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );
};

export const useExportDocument = () =>
  useMutation(
    ({ documentSuperId, documentRevisionId, selectedOrganization, documentName }) => {
      return request
        .post(
          `/gaby/documents/${documentSuperId}/actions`,
          {
            action: 'export_document',
            organization: selectedOrganization,
            revisionId: documentRevisionId,
            superId: documentSuperId,
          },
          undefined,
          undefined,
          { expectBinary: true }
        )
        .then((res) => {
          const blob = new Blob([res.data], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = res.data.name || `${documentName}.zip`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        });
    },
    {
      onSuccess: () => {
        showMessage('success', i18n('project-details.document-export-success'));
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );

export const useImportDocument = ({ selectedOrganization }) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentName }) => {
      return request.post(`/gaby/documents/import/file`, {
        name: documentName,
        organization: selectedOrganization,
      });
    },
    {
      onSuccess: () => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('dashboard.document-import-success'),
          removeTime: 3000,
        });
        queryClient.invalidateQueries([queryKeys.documents, { selectedOrganization }]);
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );
};

export const useImportableDocuments = ({ organization }) => {
  return useQuery(
    [],
    async () => {
      const response = await request.get(`/gaby/documents/importable/${organization}`);
      return response.data.files;
    },
    {
      enabled: !!organization,
      onError: ServerErrorHandler,
    }
  );
};

export function useNewDocument() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ documentName, selectedOrganization }) => {
      return request.post(`/gaby/documents`, {
        organization: selectedOrganization,
        name: documentName,
      });
    },
    {
      onSuccess: (_, { selectedOrganization }) => {
        showMessage('success', i18n('project-details.document-create-success'));
        queryClient.invalidateQueries(queryKeys.documents, { selectedOrganization });
      },
      onError: (error) => {
        errorHandler(error);
      },
    }
  );
}
